import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { VACIO, ETIQUETA_VACIO } from './filtros';
import Popover from './Popover';
import { RANGOS_FECHA, filtroDeRango } from './rangosFecha';

const etiquetaValor = (v) => (v === VACIO ? ETIQUETA_VACIO : v);

// Compara sin mayúsculas ni tildes: «ingles» encuentra «Inglés».
const normalizar = (t) =>
  String(t ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const resumen = (actuales) => {
  if (!actuales.length) return 'Todos';
  if (actuales.length === 1) return etiquetaValor(actuales[0]);
  return `${actuales.length} seleccionados`;
};

// Desplegable de selección múltiple (casillas) con el nº de registros de cada valor.
const CampoMulti = ({ col, actuales, opciones, cargando, onChange }) => {
  const [ancla, setAncla] = useState(null);
  const [texto, setTexto] = useState('');
  const conocidos = new Set(opciones.map((o) => o.valor));
  const lista = [
    ...actuales.filter((v) => !conocidos.has(v)).map((valor) => ({ valor, n: 0 })),
    ...opciones,
  ];
  const buscado = normalizar(texto.trim());
  const visibles = buscado
    ? lista.filter((o) => normalizar(etiquetaValor(o.valor)).includes(buscado))
    : lista;
  const cerrar = () => {
    setAncla(null);
    setTexto('');
  };
  const alternar = (valor) =>
    onChange(actuales.includes(valor) ? actuales.filter((v) => v !== valor) : [...actuales, valor]);

  return (
    <div className="tbl-panel-campo">
      <span className="tbl-panel-etq">{col.label}</span>
      <button
        type="button"
        className="tbl-panel-multi"
        aria-label={col.label}
        aria-haspopup="dialog"
        aria-expanded={!!ancla}
        onClick={(e) => (ancla ? cerrar() : setAncla(e.currentTarget))}
      >
        <span>{resumen(actuales)}</span>
      </button>
      <Popover ancla={ancla} onClose={cerrar} ancho={260} etiqueta={col.label}>
        <div className="tbl-panel-buscar">
          <input
            type="search"
            className="tbl-panel-buscar-input"
            placeholder={`Buscar ${col.label.toLowerCase()}…`}
            aria-label={`Buscar en ${col.label}`}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
        <div className="tbl-panel-lista">
          {cargando && <p className="tbl-panel-vacio">Cargando valores…</p>}
          {!buscado && (
            <label className="tbl-panel-op">
              <input type="checkbox" checked={!actuales.length} onChange={() => onChange([])} />
              Todos
            </label>
          )}
          {buscado && !cargando && !visibles.length && (
            <p className="tbl-panel-vacio">Sin resultados</p>
          )}
          {visibles.map((o) => (
            <label key={o.valor} className="tbl-panel-op">
              <input
                type="checkbox"
                checked={actuales.includes(o.valor)}
                onChange={() => alternar(o.valor)}
              />
              {etiquetaValor(o.valor)} ({o.n})
            </label>
          ))}
        </div>
      </Popover>
    </div>
  );
};

CampoMulti.propTypes = {
  col: PropTypes.object.isRequired,
  actuales: PropTypes.array.isRequired,
  opciones: PropTypes.array.isRequired,
  cargando: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

// Rango de fechas (desde / hasta) de una columna de tipo fecha.
const CampoFecha = ({ col, actual, onChange }) => {
  const cambiar = (clave, valor) => {
    const siguiente = { ...actual, rango: undefined, [clave]: valor || undefined };
    onChange(siguiente.desde || siguiente.hasta ? siguiente : null);
  };
  return (
    <div className="tbl-panel-campo tbl-panel-campo--fecha">
      <span className="tbl-panel-etq">{col.label}</span>
      <div className="tbl-panel-rangos">
        {RANGOS_FECHA.map((r) => {
          const f = filtroDeRango(r.id);
          const activo = actual.rango === r.id;
          return (
            <button
              key={r.id}
              type="button"
              className={`tbl-chip-rango${activo ? ' activo' : ''}`}
              aria-pressed={activo}
              onClick={() => onChange(activo ? null : { ...f, rango: r.id })}
            >
              {r.label}
            </button>
          );
        })}
      </div>
      <div className="tbl-panel-fechas">
        <input
          className="tbl-input"
          type="date"
          aria-label={`${col.label}: desde`}
          value={actual.desde || ''}
          max={actual.hasta || undefined}
          onChange={(e) => cambiar('desde', e.target.value)}
        />
        <input
          className="tbl-input"
          type="date"
          aria-label={`${col.label}: hasta`}
          value={actual.hasta || ''}
          min={actual.desde || undefined}
          onChange={(e) => cambiar('hasta', e.target.value)}
        />
      </div>
    </div>
  );
};

CampoFecha.propTypes = {
  col: PropTypes.object.isRequired,
  actual: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SIN_FACETAS = () => Promise.resolve([]);

/**
 * Panel de filtros rápidos bajo la caja de búsqueda: un desplegable por columna con sus valores
 * reales (con el nº de registros). Escribe en los mismos filtros por columna de la tabla, así que
 * se guardan por usuario y aparecen como chips en «Filtros activos».
 */
const PanelFiltros = ({ tabla, ids, loadFacets = SIN_FACETAS }) => {
  const [facetas, setFacetas] = useState({});
  const clave = JSON.stringify(tabla.filtros);

  useEffect(() => {
    let vivo = true;
    ids.forEach((id) => {
      if (tabla.columnas.find((c) => c.id === id)?.filter === 'date') return;
      Promise.resolve(loadFacets(id))
        .then((v) => vivo && setFacetas((prev) => ({ ...prev, [id]: v || [] })))
        .catch(() => {});
    });
    return () => {
      vivo = false;
    };
    // Las facetas cambian al cambiar los demás filtros.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave, ids.join(','), loadFacets]);

  const columnas = ids.map((id) => tabla.columnas.find((c) => c.id === id)).filter(Boolean);

  const elegir = (id, valores) => tabla.fijarFiltro(id, valores.length ? { valores } : null);

  return (
    <div className="tbl-panel-filtros" role="group" aria-label="Filtros">
      {columnas.map((col) =>
        col.filter === 'date' ? (
          <CampoFecha
            key={col.id}
            col={col}
            actual={tabla.filtros[col.id] || {}}
            onChange={(v) => tabla.fijarFiltro(col.id, v)}
          />
        ) : (
          <CampoMulti
            key={col.id}
            col={col}
            actuales={tabla.filtros[col.id]?.valores || []}
            opciones={facetas[col.id] || []}
            cargando={facetas[col.id] === undefined}
            onChange={(v) => elegir(col.id, v)}
          />
        ),
      )}
    </div>
  );
};

PanelFiltros.propTypes = {
  tabla: PropTypes.object.isRequired,
  ids: PropTypes.array.isRequired,
  loadFacets: PropTypes.func,
};

export default PanelFiltros;
